import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  User, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  signInWithPopup
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';

// Define user roles
export type UserRole = 'student' | 'parent' | 'teacher' | 'admin';

// Define user data structure
export interface UserData {
  uid: string;
  email: string | null;
  role: UserRole;
  displayName?: string | null;
  isSuperAdmin?: boolean;
  createdAt?: string;
}

// Define Auth context interface
interface AuthContextType {
  currentUser: User | null;
  userData: UserData | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  googleLogin: () => Promise<void>;
  signup: (email: string, password: string, role: UserRole) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: {children: ReactNode}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if this is the first user
  const checkIsFirstUser = async (): Promise<boolean> => {
    try {
      console.log("Checking if this is the first user...");
      
      // First check if any users exist
      try {
        const usersCollection = collection(db, 'users');
        const usersSnapshot = await getDocs(usersCollection);
        
        if (usersSnapshot.empty) {
          console.log("No users found - this will be the first user (admin)");
          return true;
        }
        
        console.log(`Found ${usersSnapshot.size} existing users`);
        return false;
      } catch (error) {
        console.error("Error checking users collection:", error);
        
        // Fallback: Try checking system document
        try {
          const adminTrackerRef = doc(db, 'system', 'admin-tracker');
          const adminTrackerDoc = await getDoc(adminTrackerRef);
          
          if (!adminTrackerDoc.exists()) {
            console.log("No admin tracker found - this might be the first user");
            
            // Create the tracker for future reference
            await setDoc(adminTrackerRef, { 
              adminExists: true,
              created: new Date().toISOString()
            });
            
            return true;
          }
          
          return false;
        } catch (innerError) {
          console.error("Error with admin tracker check:", innerError);
          
          // If everything fails, default to checking if we can write to users collection
          // If we can, assume this is the first user
          return true;
        }
      }
    } catch (error) {
      console.error("Error in checkIsFirstUser:", error);
      
      // In case of any error, default to true to ensure we have at least one admin
      return true;
    }
  };

  // Fetch user data from Firestore
  const fetchUserData = async (user: User) => {
    try {
      console.log(`Fetching user data for ${user.uid}`);
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        console.log("User data found:", userDoc.data());
        setUserData(userDoc.data() as UserData);
      } else {
        console.log('No user data found in Firestore');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  // Handle login
  const login = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      await fetchUserData(result.user);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Google login
  const googleLogin = async () => {
    try {
      console.log("Starting Google login process...");
      const result = await signInWithPopup(auth, googleProvider);
      console.log("Google login successful, checking user in database...");
      
      // Create or update user in Firestore
      const userDocRef = doc(db, 'users', result.user.uid);
      
      // Check if user already exists
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        // User exists, fetch their data
        console.log("Existing user found in database");
        const existingUserData = userDoc.data() as UserData;
        setUserData(existingUserData);
      } else {
        console.log("New user - checking if first user...");
        // New user - check if this is the first user (who becomes admin)
        const isFirstUser = await checkIsFirstUser();
        
        // Determine role based on whether this is the first user
        const userRole: UserRole = isFirstUser ? 'admin' : 'parent';
        
        console.log(`Creating new user with role: ${userRole}`);
        
        // Create new user profile
        const userData: UserData = {
          uid: result.user.uid,
          email: result.user.email,
          role: userRole,
          displayName: result.user.displayName,
          isSuperAdmin: isFirstUser,
          createdAt: new Date().toISOString()
        };
        
        // Write user data to Firestore
        try {
          await setDoc(userDocRef, userData);
          console.log("User data successfully written to Firestore");
          setUserData(userData);
        } catch (error) {
          console.error("Error writing user data to Firestore:", error);
          // Still set the user data in state so the user can proceed
          setUserData(userData);
        }
        
        console.log(`User created with role: ${userRole}${isFirstUser ? ' (Super Admin)' : ''}`);
      }
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  };

  // Handle signup
  const signup = async (email: string, password: string, role: UserRole) => {
    try {
      console.log("Starting signup process...");
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // For email signup, still check if this is the first user
      const isFirstUser = await checkIsFirstUser();
      
      // Override role if this is the first user
      const actualRole = isFirstUser ? 'admin' : role;
      
      // Create user in Firestore
      const userDocRef = doc(db, 'users', result.user.uid);
      const userData: UserData = {
        uid: result.user.uid,
        email: result.user.email,
        role: actualRole,
        isSuperAdmin: isFirstUser,
        createdAt: new Date().toISOString()
      };
      
      try {
        await setDoc(userDocRef, userData);
        console.log("User data successfully written to Firestore");
        setUserData(userData);
      } catch (error) {
        console.error("Error writing user data to Firestore:", error);
        // Still set the user data in state so the user can proceed
        setUserData(userData);
      }
      
      console.log(`User created with role: ${actualRole}${isFirstUser ? ' (Super Admin)' : ''}`);
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  // Handle logout
  const logout = async () => {
    try {
      await signOut(auth);
      setUserData(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("Auth state changed:", user ? `User ${user.uid}` : "No user");
      setCurrentUser(user);
      
      if (user) {
        await fetchUserData(user);
      } else {
        setUserData(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userData,
    loading,
    login,
    googleLogin,
    signup,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 